<script setup>
import { VForm } from 'vuetify/components/VForm'
import { themeConfig } from '@themeConfig'
import { useClientAccountStore } from '@/stores/clientAccount'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { safeClientRedirectPath } from '@/utils/safeClientRedirect'

definePage({
  meta: {
    layout: 'blank',
    unauthenticatedOnly: true,
    clientArea: true,
    public: false,
  },
})

const accountStore = useClientAccountStore()
const route = useRoute()
const router = useRouter()
const { t } = useI18n()

const isPasswordVisible = ref(false)
const formError = ref('')
const refVForm = ref()

const credentials = ref({
  email: '',
  password: '',
})

const login = async () => {
  formError.value = ''
  try {
    await accountStore.login(credentials.value)
    await router.replace(safeClientRedirectPath(route.query.to))
  }
  catch (err) {
    formError.value = err.payload?.error || err.message || t('clientLogin.genericError')
  }
}

const onSubmit = () => {
  refVForm.value?.validate().then(({ valid }) => {
    if (valid)
      login()
  })
}
</script>

<template>
  <div class="auth-wrapper d-flex align-center justify-center pa-4">
    <VCard
      flat
      border
      max-width="480"
      width="100%"
      class="pa-2"
    >
      <VCardText>
        <div class="d-flex align-center gap-x-3 mb-6">
          <VNodeRenderer :nodes="themeConfig.app.logo" />
          <span class="text-h5 text-high-emphasis">FitX</span>
        </div>

        <h1 class="text-h4 mb-1">
          {{ $t('clientLogin.title') }}
        </h1>
        <p class="mb-0 text-medium-emphasis">
          {{ $t('clientLogin.subtitle') }}
        </p>
      </VCardText>

      <VCardText>
        <VAlert
          v-if="formError"
          type="error"
          variant="tonal"
          class="mb-4"
          role="alert"
        >
          {{ formError }}
        </VAlert>

        <VForm
          ref="refVForm"
          @submit.prevent="onSubmit"
        >
          <VRow>
            <VCol cols="12">
              <VTextField
                v-model="credentials.email"
                :label="$t('common.email')"
                type="email"
                autocomplete="username"
                autofocus
                :rules="[requiredValidator, emailValidator]"
                :disabled="accountStore.loading"
              />
            </VCol>

            <VCol cols="12">
              <VTextField
                v-model="credentials.password"
                :label="$t('common.password')"
                :type="isPasswordVisible ? 'text' : 'password'"
                autocomplete="current-password"
                :rules="[requiredValidator]"
                :disabled="accountStore.loading"
                :append-inner-icon="isPasswordVisible ? 'ri-eye-off-line' : 'ri-eye-line'"
                @click:append-inner="isPasswordVisible = !isPasswordVisible"
              />

              <div class="d-flex justify-end my-4">
                <RouterLink
                  class="text-primary"
                  to="/client/forgot-password"
                >
                  {{ $t('clientLogin.forgotPassword') }}
                </RouterLink>
              </div>
            </VCol>

            <VCol cols="12">
              <VBtn
                block
                type="submit"
                color="primary"
                :loading="accountStore.loading"
              >
                {{ $t('clientLogin.submit') }}
              </VBtn>
            </VCol>
          </VRow>
        </VForm>
      </VCardText>
    </VCard>
  </div>
</template>

<style lang="scss">
@use "@core/scss/template/pages/page-auth";
</style>
